import { z } from 'zod'
import { oc } from '@orpc/contract'

export const bridgeEnvironmentSchema = z.enum(['sandbox', 'production'])

/**
 * Writes an organization's Bridge API key. **Every organization needs its own** — Bridge
 * issues one key per organization and there is no environment-wide default, so this write
 * is what turns Bridge on for an organization.
 *
 * The key is write-only at this boundary: it is accepted on save, stored in
 * dynamic-settings, and never returned by any endpoint. Unlike Stripe and DoseSpot there
 * is no non-secret half to echo back, so the status endpoint reports the *environment*
 * instead — derived from the key's own prefix, revealing nothing while still catching the
 * mistake that matters, a sandbox key pasted into a production organization.
 *
 * The key **is** validated live before being persisted (`GET /api/payers`, a cheap read
 * with no side effects), following the Stripe writer rather than the DoseSpot one: a
 * rejected key here is silent until a claim fails, which is far too late to learn about it.
 */
export const setOrganizationBridgeKey = oc
  .route({ method: 'POST', path: '/v2/super/organizations/{organizationId}/bridge-key' })
  .input(
    z.object({
      organizationId: z.string().min(1),
      // Bridge documents no shape beyond the `sk_sandbox_` / `sk_` prefixes, and pinning
      // an exact format here would reject a key Bridge itself accepts. The live check in
      // the handler is the real assertion.
      apiKey: z.string().trim().min(1),
      // Svix's per-endpoint signing secret, copied from Bridge's dashboard after the
      // organization registers its own /webhooks/bridge/{organizationId} endpoint. No live
      // check exists for this one (Bridge exposes nothing to verify a Svix secret against),
      // so — unlike apiKey — it is simply stored as given. Optional: an organization can
      // save its API key before it has registered a webhook endpoint at all.
      webhookSecret: z.string().trim().min(1).optional(),
    }),
  )
  .output(
    z.object({
      configured: z.literal(true),
      environment: bridgeEnvironmentSchema,
      /** How many payers this organization can reach — the claimable set, not the catalogue. */
      payerCount: z.number().int().min(0),
    }),
  )

export const getOrganizationBridgeStatus = oc
  .route({ method: 'GET', path: '/v2/super/organizations/{organizationId}/bridge-key' })
  .input(z.object({ organizationId: z.string().min(1) }))
  .output(
    z.object({
      configured: z.boolean(),
      environment: bridgeEnvironmentSchema.optional(),
      /** Whether a webhook secret is on file — never the value itself. */
      webhookSecretConfigured: z.boolean(),
    }),
  )
